import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchUserAccess, fetchUserAccount, UserAccessPermission, isWrappedUserAccess, type LeadershipNavigation } from '../api/entityApi';
import type { EntityScope, UserAccount } from '../types/entity';
import { ActionCode } from '../types/actionCodes';
import { getScopeFromValue } from '@features/invitations/utils/getScopeFromParam';
import {
  SESSION_STORAGE_KEYS,
  entityNameCacheKey,
} from '@features/auth/constants/sessionStorageKeys';

/** Workspaces confirmed for this user in this page session (key SCOPE_id). Denials are re-checked. */
const grantedAccountCache = new Map<string, UserAccount>();

/** Forget confirmed workspaces (e.g. on sign-out or in tests). */
export const clearEntityAccessCache = () => grantedAccountCache.clear();

interface UsePermissionsProps {
  scopeType?: string;
  scopeId?: number;
  actionCodes: string[];
  /** When set, only permissions for this department are returned from the API. */
  departmentId?: number;
  /** When true, response includes leadership navigation flags for sidebar gating. */
  includeLeadership?: boolean;
}

export const usePermissions = ({
  scopeType,
  scopeId,
  actionCodes,
  departmentId,
  includeLeadership = false,
}: UsePermissionsProps) => {
  const actionCodesKey = useMemo(
    () => JSON.stringify([...actionCodes].sort()),
    [actionCodes]
  );
  const normalizedActionCodes = useMemo(
    () => JSON.parse(actionCodesKey) as string[],
    [actionCodesKey]
  );
  const willCheck = Boolean(scopeType && scopeId && normalizedActionCodes.length > 0);

  const [permittedPermissions, setPermittedPermissions] = useState<UserAccessPermission[]>([]);
  const [leadership, setLeadership] = useState<LeadershipNavigation | null>(null);
  // Start loading when a scoped check will run so callers do not treat "not yet fetched"
  // as denied (e.g. Navigate on first paint before POST /user/access settles).
  const [loading, setLoading] = useState(willCheck);
  const [error, setError] = useState<string | null>(null);

  const checkPermissions = useCallback(async () => {
    if (!scopeType || !scopeId || normalizedActionCodes.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setPermittedPermissions([]);
      setLeadership(null);

      const response = await fetchUserAccess(
        scopeType,
        scopeId,
        normalizedActionCodes,
        {
          departmentId,
          includeLeadership,
        }
      );

      if (response.isSuccess() && response.data) {
        const data = response.data;
        if (isWrappedUserAccess(data)) {
          setPermittedPermissions(data.permissions);
          setLeadership(data.leadership);
        } else {
          setPermittedPermissions(data);
          setLeadership(null);
        }
      } else {
        const errorData = response.getError?.() || response.error;
        console.error('Permission check failed:', errorData);
        setError(errorData?.detail || 'Failed to check permissions');
        setLeadership(null);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setError('An error occurred while checking permissions');
      setLeadership(null);
    } finally {
      setLoading(false);
    }
  }, [normalizedActionCodes, scopeType, scopeId, departmentId, includeLeadership]);

  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  const hasPermission = useCallback(
    (actionCode: ActionCode | string): boolean => {
      return permittedPermissions.some((p) => p.action_code === actionCode);
    },
    [permittedPermissions]
  );

  const hasAnyPermission = useCallback(
    (codes: (ActionCode | string)[]): boolean => {
      return codes.some((code) => permittedPermissions.some((p) => p.action_code === code));
    },
    [permittedPermissions]
  );

  const hasAllPermissions = useCallback(
    (codes: (ActionCode | string)[]): boolean => {
      return codes.every((code) => permittedPermissions.some((p) => p.action_code === code));
    },
    [permittedPermissions]
  );

  return {
    permittedPermissions,
    leadership,
    /** @deprecated Use permittedPermissions; unique action codes for menu compatibility. */
    permittedActions: useMemo(
      () => [...new Set(permittedPermissions.map((p) => p.action_code))],
      [permittedPermissions]
    ),
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refresh: checkPermissions,
  };
};

// Hook to get current entity context from the URL (source of truth),
// falling back to the last known entity context for personal pages
// like /user/profile or /invitations that still need the sidebar.
export const useEntityContext = () => {
  const location = useLocation();
  const entityMatch = useMemo(
    () => location.pathname.match(/^\/(nation|district|church)\/(\d+)/),
    [location.pathname]
  );
  const isEntityRoute = !!entityMatch;

  // Derive scope + id from URL (or fall back to cache for personal pages)
  const { scope, scopeId } = useMemo(() => {
    if (entityMatch) {
      return { scope: getScopeFromValue(entityMatch[1]), scopeId: parseInt(entityMatch[2], 10) };
    }

    // Not on an entity-scoped route — restore previous context
    try {
      const cached = sessionStorage.getItem(SESSION_STORAGE_KEYS.LAST_ENTITY_CONTEXT);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.scope && parsed?.scopeId) {
          return { scope: parsed.scope as string, scopeId: parsed.scopeId as number };
        }
      }
    } catch { /* ignore */ }

    // No remembered workspace (e.g. opened from My Workspaces) ??? no sidebar
    return { scope: '', scopeId: 0 };
  }, [entityMatch]);

  // Start with cached name or fallback
  const cacheKey = scope && scopeId ? entityNameCacheKey(scope, scopeId) : '';
  const cachedName = cacheKey ? sessionStorage.getItem(cacheKey) : null;
  const fallbackName = scope ? scope.charAt(0) + scope.slice(1).toLowerCase() : '';

  const [scopeName, setScopeName] = useState(cachedName || fallbackName);
  // Access check: 'loading' until accounts are verified, then 'granted' or 'denied'
  const [accessStatus, setAccessStatus] = useState<'loading' | 'granted' | 'denied'>(
    isEntityRoute ? 'loading' : 'granted'
  );

  // When scope/id changes, verify access and fetch entity names from API
  useEffect(() => {
    if (!scope || !scopeId) return;

    const key = entityNameCacheKey(scope, scopeId);
    const existing = sessionStorage.getItem(key);

    if (existing) {
      setScopeName(existing);
    } else {
      setScopeName(scope.charAt(0) + scope.slice(1).toLowerCase());
    }

    if (isEntityRoute) {
      setAccessStatus('loading');
    }

    let isCurrent = true;
    const accessKey = `${scope}_${scopeId}`;

    const applyAccount = (found: UserAccount | null) => {
      if (found?.scope_name) {
        sessionStorage.setItem(key, found.scope_name);
        setScopeName(found.scope_name);
        setAccessStatus('granted');
      } else if (isEntityRoute) {
        setAccessStatus('denied');
      } else {
        // Personal pages with a restored context ??? treat as usable sidebar context
        setAccessStatus('granted');
      }
    };

    const cachedAccount = grantedAccountCache.get(accessKey);
    if (cachedAccount) {
      applyAccount(cachedAccount);
      return;
    }

    // Look up just this entity (scope + scopeRefId, perPage=1) so workspaces beyond
    // the first page of the accounts list are found too.
    fetchUserAccount(scope as EntityScope, scopeId)
      .then((found) => {
        if (found?.scope_name) grantedAccountCache.set(accessKey, found);
        if (isCurrent) applyAccount(found);
      })
      .catch(() => {
        if (isCurrent && isEntityRoute) setAccessStatus('denied');
      });

    return () => {
      isCurrent = false;
    };
  }, [scope, scopeId, isEntityRoute]);

  // Build context; only persist lastEntityContext after access is granted
  // so denied deep links do not leak workspace context into the next session.
  const context = useMemo(() => {
    if (!scope || !scopeId) return null;
    const ctx = { scope, scopeId, scopeName };
    if (accessStatus === 'granted') {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.LAST_ENTITY_CONTEXT, JSON.stringify(ctx));
    }
    return ctx;
  }, [scope, scopeId, scopeName, accessStatus]);

  return { context, accessStatus };
};
